import { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Holding } from '../api/portfolio';
import { getCompanyColor } from '../utils/companyColors';
import { getCompanyName } from '../utils/companyNames';

interface PortfolioAreaChartProps {
  portfolio: Holding[];
}

type TimePeriod = '1D' | '1M' | '1Y' | '5Y';

export default function PortfolioAreaChart({ portfolio }: PortfolioAreaChartProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('1M');
  const [selectedStocks, setSelectedStocks] = useState<string[]>(portfolio.map(h => h.symbol));

  if (portfolio.length === 0) {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Portfolio Growth Over Time</h3>
        <div className="flex items-center justify-center h-64 text-gray-500">
          Add holdings to see your portfolio growth
        </div>
      </div>
    );
  }

  // Generate mock historical data based on selected period
  const getPeriodsForTimeframe = (period: TimePeriod) => {
    switch (period) {
      case '1D':
        return ['9:30', '11:00', '12:30', '14:00', '15:30', '16:00'];
      case '1M':
        return ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
      case '1Y':
        return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      case '5Y':
        return ['2020', '2021', '2022', '2023', '2024'];
      default:
        return ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
    }
  };

  const periods = getPeriodsForTimeframe(selectedPeriod);
  
  // Filter portfolio based on selected stocks
  const displayedHoldings = portfolio.filter(h => selectedStocks.includes(h.symbol));
  
  const chartData = periods.map((period, index) => {
    const dataPoint: any = { period };
    
    displayedHoldings.forEach(holding => {
      // Simulate growth over time (mock data)
      const growthFactor = 1 + (index * 0.05) + (Math.random() * 0.1 - 0.05);
      dataPoint[holding.symbol] = (holding.totalValue || 0) * growthFactor;
    });
    
    return dataPoint;
  });
  
  const toggleStock = (symbol: string) => {
    setSelectedStocks(prev => 
      prev.includes(symbol) 
        ? prev.filter(s => s !== symbol)
        : [...prev, symbol]
    );
  };
  
  const toggleAll = () => {
    if (selectedStocks.length === portfolio.length) {
      setSelectedStocks([]);
    } else {
      setSelectedStocks(portfolio.map(h => h.symbol));
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum: number, item: any) => sum + item.value, 0);
      
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          <p className="text-sm font-medium text-gray-700 mb-2">
            Total: ${total.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </p>
          <div className="space-y-1">
            {payload.reverse().map((item: any) => (
              <div key={item.dataKey} className="flex items-center justify-between gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: item.color }}
                  />
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-900">{item.dataKey}</span>
                    <span className="text-xs text-gray-500">{getCompanyName(item.dataKey)}</span>
                  </div>
                </div>
                <span className="font-medium text-gray-900">
                  ${item.value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const timePeriods: TimePeriod[] = ['1D', '1M', '1Y', '5Y'];

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Portfolio Growth Over Time</h3>
        
        {/* Time Period Selector */}
        <div className="flex gap-2">
          {timePeriods.map((period) => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedPeriod === period
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {period}
            </button>
          ))}
        </div>
      </div>

      {/* Stock Selector */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Select Holdings to Display:</span>
          <button
            onClick={toggleAll}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            {selectedStocks.length === portfolio.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {portfolio.map((holding, index) => (
            <button
              key={holding.symbol}
              onClick={() => toggleStock(holding.symbol)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                selectedStocks.includes(holding.symbol)
                  ? 'bg-white border-2 text-gray-900 shadow-sm'
                  : 'bg-gray-200 text-gray-500 border-2 border-transparent'
              }`}
              style={selectedStocks.includes(holding.symbol) ? { borderColor: getCompanyColor(holding.symbol, index) } : {}}
            >
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: getCompanyColor(holding.symbol, index) }}
              />
              {holding.symbol}
            </button>
          ))}
        </div>
      </div>
      
      <ResponsiveContainer width="100%" height={400}>
        <AreaChart data={chartData}>
          <defs>
            {portfolio.map((holding, index) => (
              <linearGradient key={holding.symbol} id={`color${holding.symbol}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={getCompanyColor(holding.symbol, index)} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={getCompanyColor(holding.symbol, index)} stopOpacity={0.3}/>
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            dataKey="period" 
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
          />
          <YAxis 
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ fontSize: '12px' }}
            iconType="square"
          />
          {portfolio.map((holding, index) => (
            <Area
              key={holding.symbol}
              type="monotone"
              dataKey={holding.symbol}
              stackId="1"
              stroke={getCompanyColor(holding.symbol, index)}
              fill={`url(#color${holding.symbol})`}
              strokeWidth={2}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
